import { Routes } from '@angular/router';
import { AppLayout } from './layout/component/app.layout';
import { HomeComponent } from './home/home';
import { ProfileComponent } from './profile/profile';
import { permissionGuard } from './core/guards/permission.guard';
import { AppPermission } from './core/shared/constants';

export const routes: Routes = [
  {
    path: '',
    component: AppLayout,
    children: [
      { path: 'home', component: HomeComponent },
      { path: 'profile', component: ProfileComponent },
      {
        path: 'employee',
        canActivate: [permissionGuard(AppPermission.EMPLOYEES_VIEW)],
        loadChildren: () => import('./employee/employee.routes').then(m => m.EMPLOYEE_ROUTES)
      },
      {
        path: 'department',
        canActivate: [permissionGuard(AppPermission.DEPARTMENTS_VIEW)],
        loadChildren: () => import('./department/department.routes').then(m => m.DEPARTMENT_ROUTES)
      },
      {
        path: 'department-balances',
        canActivate: [permissionGuard(AppPermission.CASH_TRANSACTIONS_VIEW)],
        loadChildren: () => import('./department-balance/department-balance.routes').then(m => m.DEPARTMENT_BALANCE_ROUTES)
      },
      {
        path: 'department-accounts',
        canActivate: [permissionGuard(AppPermission.CASH_TRANSACTIONS_VIEW)],
        loadChildren: () => import('./department-account/department-account.routes').then(m => m.DEPARTMENT_ACCOUNT_ROUTES)
      },
      {
        path: 'cash-transactions',
        canActivate: [permissionGuard(AppPermission.CASH_TRANSACTIONS_VIEW)],
        loadChildren: () => import('./cash-transaction/cash-transaction.routes').then(m => m.CASH_TRANSACTION_ROUTES)
      },
      {
        path: 'organization',
        canActivate: [permissionGuard(AppPermission.ORGANIZATIONS_VIEW)],
        loadChildren: () => import('./organization/organization.routes').then(m => m.ORGANIZATION_ROUTES)
      },
      {
        path: 'supplier',
        canActivate: [permissionGuard(AppPermission.SUPPLIERS_VIEW)],
        loadChildren: () => import('./supplier/supplier.routes').then(m => m.SUPPLIER_ROUTES)
      },
      {
        path: 'buyer',
        canActivate: [permissionGuard(AppPermission.BUYERS_VIEW)],
        loadChildren: () => import('./buyer/buyer.routes').then(m => m.BUYER_ROUTES)
      },
      {
        path: 'repair-requests',
        canActivate: [permissionGuard(AppPermission.REPAIR_REQUESTS_VIEW)],
        loadChildren: () => import('./repair-request/repair-request.routes').then(m => m.REPAIR_REQUEST_ROUTES)
      },
      {
        path: 'permit-requests',
        canActivate: [permissionGuard(AppPermission.PERMIT_REQUESTS_VIEW)],
        loadChildren: () => import('./permit-request/permit-request.routes').then(m => m.PERMIT_REQUEST_ROUTES)
      },
      {
        path: 'permit-board',
        canActivate: [permissionGuard(AppPermission.PERMIT_REQUESTS_VIEW)],
        loadChildren: () => import('./permit-board/permit-board.routes').then(m => m.PERMIT_BOARD_ROUTES)
      },
      // Дошка оператора НЕ має гварда навмисно (§3.4). Доступ до `/permit-tasks/*` дає
      // рядок у `permit_operators`, а не дозвіл: гвард на будь-якому дозволі відрізав би
      // продавця, якого призначили оператором без зміни ролі, від його ж роботи.
      // Права вирішує сервер — селектор видів показує «ви не оператор» за `has_board_access`.
      {
        path: 'permit-operator-board',
        loadChildren: () => import('./permit-operator-board/permit-operator-board.routes').then(m => m.PERMIT_OPERATOR_BOARD_ROUTES)
      },
      // `MANAGE`, а не `VIEW`, як у решти екранів дозволів: 21.09 бекенд закрив обидва роути
      // `/permit-reports/*` на `permit_requests.manage`. Продавцю вони віддають `403` навіть
      // по його власному магазину, тож `VIEW` тут пускав би на екран, який гарантовано
      // відповість помилкою.
      {
        path: 'permit-reports',
        canActivate: [permissionGuard(AppPermission.PERMIT_REQUESTS_MANAGE)],
        loadChildren: () => import('./permit-report/permit-report.routes').then(m => m.PERMIT_REPORT_ROUTES)
      },
      {
        path: 'permit-operators',
        canActivate: [permissionGuard(AppPermission.PERMIT_REQUESTS_MANAGE)],
        loadChildren: () => import('./permit-operator/permit-operator.routes').then(m => m.PERMIT_OPERATOR_ROUTES)
      },
      {
        path: 'receipt-invoices',
        canActivate: [permissionGuard(AppPermission.DOCUMENTS_RECEIPT_INVOICES_VIEW)],
        loadChildren: () => import('./receipt-invoice/document-receipt-invoice.routes').then(m => m.DOCUMENT_RECEIPT_INVOICE_ROUTES)
      },
      {
        path: 'expense-invoices',
        canActivate: [permissionGuard(AppPermission.DOCUMENTS_EXPENSE_INVOICES_VIEW)],
        loadChildren: () => import('./expense-invoice/expense-invoice.routes').then(m => m.EXPENSE_INVOICE_ROUTES),
      },
      {
        path: 'buyer-returns',
        canActivate: [permissionGuard(AppPermission.DOCUMENTS_BUYER_RETURNS_VIEW)],
        loadChildren: () => import('./buyer-return/buyer-return.routes').then(m => m.BUYER_RETURN_ROUTES),
      },
      {
        path: 'revaluation-acts',
        canActivate: [permissionGuard(AppPermission.DOCUMENTS_REVALUATION_ACTS_VIEW)],
        loadChildren: () => import('./revaluation-act/revaluation-act.routes').then(m => m.REVALUATION_ACT_ROUTES),
      },
      {
        path: 'exchange-rates',
        canActivate: [permissionGuard(AppPermission.EXCHANGE_RATES_VIEW)],
        loadChildren: () => import('./exchange-rate/exchange-rate.routes').then(m => m.EXCHANGE_RATE_ROUTES)
      },
      {
        path: 'system-settings',
        canActivate: [permissionGuard(AppPermission.SYSTEM_MANAGE)],
        loadChildren: () => import('./system-setting/system-setting.routes').then(m => m.SYSTEM_SETTING_ROUTES)
      },
      {
        path: 'product-categories',
        canActivate: [permissionGuard(AppPermission.PRODUCT_CATEGORIES_VIEW)],
        loadChildren: () => import('./product-category/product-category.routes').then(m => m.PRODUCT_CATEGORY_ROUTES)
      },
      {
        path: 'brands',
        canActivate: [permissionGuard(AppPermission.PRODUCTS_VIEW)],
        loadChildren: () => import('./brand/brand.routes').then(m => m.BRAND_ROUTES)
      },
      {
        path: 'products',
        canActivate: [permissionGuard(AppPermission.PRODUCTS_VIEW)],
        loadChildren: () => import('./product/product.routes').then(m => m.PRODUCT_ROUTES)
      },
      {
        path: 'stock-balances',
        canActivate: [permissionGuard(AppPermission.PRODUCTS_VIEW)],
        loadChildren: () => import('./stock-balance/stock-balance.routes').then(m => m.STOCK_BALANCE_ROUTES)
      },
      {
        path: 'stock-batches',
        canActivate: [permissionGuard(AppPermission.PRODUCTS_VIEW)],
        loadChildren: () => import('./stock-batch/stock-batch.routes').then(m => m.STOCK_BATCH_ROUTES)
      },
      {
        path: 'migration',
        canActivate: [permissionGuard(AppPermission.MIGRATION_MANAGE)],
        loadChildren: () => import('./migration/migration.routes').then(m => m.MIGRATION_ROUTES)
      },
      { path: '', redirectTo: 'home', pathMatch: 'full' },
    ]
  },
  {
    path: 'access-denied',
    loadComponent: () => import('./access-denied/access-denied.component').then(m => m.AccessDeniedComponent)
  },
  { path: '**', redirectTo: 'home' }
];
